import { FC, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../../common/CustomButton';
import CustomInput from '../../common/CustomInput';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAddTagMutation, useDeleteTagMutation, useUpdateTagMutation } from '../../store/services/tagsApi';
import { useAppDispatch } from '../../hooks/redux';
import { setTags, Tag } from '../../store/slices/tagsSlice';
import { randomTagColor } from '../../utils/randomTagColor';
import styles from './Tags.module.scss';
import { DeleteIcon } from '../Icons';

const FALLBACK_TAG_COLOR = '#E0E0E0';

type TagsProps = {
  openManageFilters: boolean;
  tags: Tag[] | null;
  toggleManageDrawer: (arg: boolean) => void;
};

const Tags: FC<TagsProps> = ({ openManageFilters = false, tags, toggleManageDrawer }) => {
  const dispatch = useAppDispatch();
  const [addTagMutation] = useAddTagMutation();
  const [deleteTagMutation] = useDeleteTagMutation();
  const [updateTagMutation] = useUpdateTagMutation();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [tag, setTag] = useState<Tag | null>(null);
  const [tagName, setTagName] = useState<string>('');
  const [selectedTagColor, setSelectedTagColor] = useState<string | null>(null);
  const [openTagCreating, setOpenTagCreating] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const handleCreateTag = () => {
    setOpenTagCreating(true);
    setIsEditing(false);
    setTagName('');
    setSelectedTagColor(randomTagColor());
  };

  const handleDeleteTag = async () => {
    if (!tag?.id) return;
    try {
      await deleteTagMutation({ id: tag?.id });
      dispatch(setTags((tags ?? []).filter((t): t is Tag => t != null && t.id !== tag?.id)));
      setOpenModal(false);
      setTag(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditTag = (tag: Tag) => {
    setTagName(tag.name);
    setOpenTagCreating(true);
    setSelectedTagColor(tag.color ?? randomTagColor());
    setIsEditing(true);
    setSelectedTagId(tag.id);
  };

  const handleOpenModal = (tag: Tag) => {
    setOpenModal(true);
    setTag(tag);
  };

  const handleAddTag = async () => {
    const colorForRequest = selectedTagColor ?? randomTagColor();

    if (isEditing) {
      if (!selectedTagId) return;
      try {
        await updateTagMutation({ id: selectedTagId, name: tagName, color: colorForRequest }).unwrap();
        dispatch(
          setTags(
            (tags ?? [])
              .filter((t): t is Tag => t != null)
              .map(tag => (tag.id === selectedTagId ? { ...tag, name: tagName, color: colorForRequest } : tag))
          )
        );
      } catch (error) {
        console.error('Error updating tag:', error);
      }
    } else {
      try {
        const created = await addTagMutation({ name: tagName, color: colorForRequest }).unwrap();
        dispatch(setTags([...(tags ?? []).filter((t): t is Tag => t != null), created]));
        setTagName('');
      } catch (error) {
        console.error('Error adding tag:', error);
      }
    }
  };

  return (
    <>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: 'fit-content',
            minWidth: 355,
            width: '100%',
            bgcolor: 'background.paper',
            borderRadius: '20px',
            p: '32px 32px',
            overflowY: 'auto',
            boxShadow: 'none',
            maxHeight: '90vh',
            '@media (max-width: 900px)': {
              p: '32px 15px',
            },
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
            <CloseIcon
              sx={{ width: '30px', height: '30px' }}
              onClick={() => setOpenModal(false)}
            />
          </Box>
          <Box className={styles.modalContentWrapper}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 'bold', textAlign: 'center', color: '#222B3A' }}
            >
              Are you sure?
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#222B3A', textAlign: 'center' }}
            >
              {`You want to remove the tag “${tag?.name}”?`}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                gap: '16px',
                '& button': {
                  height: '44px',
                },
              }}
            >
              <CustomButton
                variant="outlined"
                onClick={() => setOpenModal(false)}
                customClass={styles.cancelBtn}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleDeleteTag}
                customClass={styles.editBtnDelete}
              >
                Delete tag
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Box className={`${styles.customDrawer} ${openManageFilters && styles.isShownManageTags}`}>
        <Box className={styles.customDrawerHeader}>
          <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
            <Typography
              variant="h5Bold"
              sx={{ color: '#222B3A' }}
            >
              Tags management
            </Typography>
          </Box>
          <CloseIcon
            className={styles.closeIcon}
            onClick={() => toggleManageDrawer(false)}
          />
        </Box>
        <Box className={styles.customDrawerContent}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <Typography
              variant="h9Bold"
              sx={{ color: '#222B3A' }}
            >
              Active Tags
            </Typography>
            <CustomButton
              variant="outlined"
              onClick={handleCreateTag}
            >
              + Create new
            </CustomButton>
          </Box>

          {(tags ?? [])
            .filter((t): t is Tag => t != null && t.id != null)
            .map(tag => (
              <Box
                key={tag.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  margin: '20px 0',
                }}
              >
                <Typography
                  variant="h12Semibold"
                  sx={{
                    bgcolor: tag.color ?? FALLBACK_TAG_COLOR,
                    padding: '5px 10px',
                    borderRadius: '16px',
                    height: '100%',
                    color: '#222B3A',
                  }}
                >
                  {tag.name}
                </Typography>

                <Box>
                  <DeleteIcon
                    onClick={() => handleOpenModal(tag)}
                    className={styles.binIcon}
                  />
                  <EditOutlinedIcon
                    onClick={() => handleEditTag(tag)}
                    className={styles.penIcon}
                  />
                </Box>
              </Box>
            ))}

          {openTagCreating && (
            <Box sx={{ marginTop: '20px', paddingBottom: '20px' }}>
              <CustomInput
                inputLabel=""
                value={tagName}
                onChange={e => setTagName(e.target.value)}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  m: '15px 0px',
                }}
              >
                <Typography variant="h11Semibold">Custom color</Typography>
                <input
                  type="color"
                  aria-label="Pick tag color"
                  value={selectedTagColor || FALLBACK_TAG_COLOR}
                  onChange={e => setSelectedTagColor(e.target.value)}
                  style={{
                    width: 44,
                    height: 32,
                    border: '1px solid transparent',
                    borderRadius: 8,
                    cursor: 'pointer',
                    padding: 0,
                    background: 'transparent',
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '10px' }}>
                <CustomButton
                  variant="outlined"
                  onClick={() => setOpenTagCreating(false)}
                  customClass={styles.editBtn}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleAddTag}
                  disabled={
                    tagName.length === 0 ||
                    (tags ?? []).some(
                      tag => tag != null && tag.name === tagName && (!isEditing || tag.id !== selectedTagId)
                    )
                  }
                  customClass={styles.editBtn}
                >
                  {isEditing ? 'Edit tag' : 'Add tag'}
                </CustomButton>
              </Box>
              {(tags ?? []).some(
                tag => tag != null && tag.name === tagName && (!isEditing || tag.id !== selectedTagId)
              ) && (
                <Typography
                  variant="h10"
                  sx={{ color: '#FF4D4F', marginTop: '10px' }}
                >
                  Tag name already exists. Please choose another name.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Tags;
